import React, { useState, useRef, useEffect } from 'react';
import { Pen, Type, Upload, Trash, Check, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, AlertCircle, Edit } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import { API_URL } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

/**
 * Enhanced SignatureCanvas - A component for capturing user signatures via drawing, typing, or uploading
 * and allowing placement directly on a document.
 * 
 * @param {Object} props
 * @param {Function} props.onSignatureCreated - Callback when a signature is created and placed, receives data URL, type, position, and page
 * @param {Function} props.onCancel - Callback when the user cancels
 * @param {string} props.initialSignatureType - Initial signature type ('drawn', 'typed', 'image')
 * @param {Object} props.documentData - Document metadata including URL and page count
 * @param {Array} props.signatureFields - Pre-defined signature fields (optional)
 * @param {Object} props.initialField - Initial signature field to use (optional)
 * @param {number} props.initialPage - Initial page to show (optional)
 */
const EnhancedSignatureCanvas = ({ 
  onSignatureCreated, 
  onCancel, 
  initialSignatureType = 'drawn',
  documentData,
  signatureFields = [],
  initialField = null,
  initialPage = 1
}) => {
  const { user } = useAuth();
  
  // SIGNATURE CREATION STATES
  const [signatureType, setSignatureType] = useState(initialSignatureType); // Use the prop instead of hardcoded 'typed'
  const [typedSignature, setTypedSignature] = useState('');
  const [typedSignatureFont, setTypedSignatureFont] = useState('Inter');
  const [fontCategory, setFontCategory] = useState('professional');
  const [imageSignature, setImageSignature] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [legalCheckboxChecked, setLegalCheckboxChecked] = useState(false);
  
  // DEFAULT SIGNATURE STATES
  const [defaultSignature, setDefaultSignature] = useState(null);
  const [isLoadingDefault, setIsLoadingDefault] = useState(false);
  const [useDefaultSignature, setUseDefaultSignature] = useState(false);
  
  // LEGAL AGREEMENT STATE
  const [hasReadContract, setHasReadContract] = useState(false);
  
  // DRAWING CANVAS STATES
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  
  // DOCUMENT STATES
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [zoom, setZoom] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);
  
  // Reference to the initial field
  const initialFieldRef = useRef(initialField);
  
  // PLACEMENT STATES
  const [activeTab, setActiveTab] = useState('create'); // 'create' or 'place'
  const [createdSignatureData, setCreatedSignatureData] = useState(null);
  const [createdSignatureType, setCreatedSignatureType] = useState(null);
  const [signaturePosition, setSignaturePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // REFS
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const signatureRef = useRef(null);
  const documentRef = useRef(null);
  
  // Available fonts for typed signatures
  const availableFonts = [
    // Professional & Business
    { name: 'Inter', css: "'Inter', sans-serif", category: 'professional' },
    { name: 'Roboto', css: "'Roboto', sans-serif", category: 'professional' },
    { name: 'Open Sans', css: "'Open Sans', sans-serif", category: 'professional' },
    { name: 'Lato', css: "'Lato', sans-serif", category: 'professional' },
    { name: 'Montserrat', css: "'Montserrat', sans-serif", category: 'professional' },
    { name: 'Poppins', css: "'Poppins', sans-serif", category: 'professional' },
    { name: 'Source Sans Pro', css: "'Source Sans Pro', sans-serif", category: 'professional' },
    
    // Elegant Scripts
    { name: 'Dancing Script', css: "'Dancing Script', cursive", category: 'elegant' },
    { name: 'Great Vibes', css: "'Great Vibes', cursive", category: 'elegant' },
    { name: 'Allura', css: "'Allura', cursive", category: 'elegant' },
    { name: 'Pinyon Script', css: "'Pinyon Script', cursive", category: 'elegant' },
    
    // Modern Scripts
    { name: 'Alex Brush', css: "'Alex Brush', cursive", category: 'modern' },
    { name: 'Kaushan Script', css: "'Kaushan Script', cursive", category: 'modern' },
    { name: 'Yellowtail', css: "'Yellowtail', cursive", category: 'modern' },
    
    // Casual Handwriting
    { name: 'Pacifico', css: "'Pacifico', cursive", category: 'casual' },
    { name: 'Satisfy', css: "'Satisfy', cursive", category: 'casual' },
    { name: 'Caveat', css: "'Caveat', cursive", category: 'casual' },
    { name: 'Indie Flower', css: "'Indie Flower', cursive", category: 'casual' },
    
    // Classic & Professional
    { name: 'Sacramento', css: "'Sacramento', cursive", category: 'classic' },
    { name: 'Norican', css: "'Norican', cursive", category: 'classic' },
    { name: 'Kalam', css: "'Kalam', sans-serif", category: 'classic' },
  ];
  
  // Initialize fonts and canvas on component mount
  useEffect(() => {
    // Get all available fonts in the document head
    document.head.insertAdjacentHTML('beforeend', `
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Open+Sans:wght@400;600;700&family=Lato:wght@400;700&family=Montserrat:wght@400;600;700&family=Poppins:wght@400;500;600;700&family=Source+Sans+Pro:wght@400;600;700&family=Dancing+Script&family=Great+Vibes&family=Pacifico&family=Sacramento&family=Satisfy&family=Allura&family=Alex+Brush&family=Kaushan+Script&family=Yellowtail&family=Pinyon+Script&family=Caveat&family=Indie+Flower&family=Norican&family=Kalam:wght@400;700&display=swap" rel="stylesheet">
    `);
    
    // Load user's default signature
    loadDefaultSignature();
  }, []);

  // Load user's default signature
  const loadDefaultSignature = async () => {
    setIsLoadingDefault(true);
    try {
      const response = await fetch(`${API_URL}/users/default-signature`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load default signature');
      }
      
      const data = await response.json();
      if (data.defaultSignature) {
        const sig = data.defaultSignature;
        setDefaultSignature(sig);
        
        // Auto-apply default signature if it exists
        if (sig.data) {
          setUseDefaultSignature(true);
          setSignatureType(sig.type);
          setCreatedSignatureData(sig.data);
          setCreatedSignatureType(sig.type);
          
          if (sig.type === 'typed') {
            // For typed signatures, we need to extract the original text
            // The sig.data contains the base64 image, but we need the original text
            // We'll store the original text in a separate field or extract it from the signature
            setTypedSignature(sig.originalText || ''); // Use original text if available
            setTypedSignatureFont(sig.font || 'Inter');
          } else if (sig.type === 'image' || sig.type === 'drawn') {
            setImageSignature(sig.data);
            setImagePreview(sig.data);
          }
        }
      }
    } catch (error) {
      console.error('Error loading default signature:', error);
      // Don't show error as user might not have a default signature
    } finally {
      setIsLoadingDefault(false);
    }
  };

  // Initialize canvas when switching to drawing mode
  useEffect(() => {
    if (signatureType === 'drawn') {
      // Small delay to ensure canvas is rendered
      setTimeout(() => {
        initializeCanvas();
      }, 100);
    } else {
      // Reset canvas context when switching away from drawing mode
      contextRef.current = null;
    }
  }, [signatureType]);

  // Reinitialize canvas when window resizes
  useEffect(() => {
    const handleResize = () => {
      if (signatureType === 'drawn' && canvasRef.current) {
        contextRef.current = null; // Reset context
        setTimeout(() => {
          initializeCanvas();
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [signatureType]);
  
  // Effect to automatically select first font when category changes
  useEffect(() => {
    const filteredFonts = availableFonts.filter(font => font.category === fontCategory);
    if (filteredFonts.length > 0 && !filteredFonts.find(f => f.name === typedSignatureFont)) {
      setTypedSignatureFont(filteredFonts[0].name);
    }
  }, [fontCategory, availableFonts, typedSignatureFont]);

  // Effect to center signature initially when moving to placement tab
  useEffect(() => {
    if (documentRef.current && createdSignatureData && activeTab === 'place') {
      const docRect = documentRef.current.getBoundingClientRect();
      // Calculate position in document coordinate space (unscaled)
      setSignaturePosition({
        x: (docRect.width / zoom) / 2 - 100,
        y: (docRect.height / zoom) / 2 - 50
      });
    }
  }, [createdSignatureData, activeTab, zoom]);
  
  // Error handling for missing document data when in placement mode
  useEffect(() => {
    if (activeTab === 'place') {
      if (!documentData) {
        setError('No document data provided');
      } else if (!documentData.url) {
        setError('Document URL is missing');
      } else {
        setError(null);
      }
    }
  }, [documentData, activeTab]);
  
    // Initialize canvas context when needed
  const initializeCanvas = () => {
    if (canvasRef.current && !contextRef.current) {
      const canvas = canvasRef.current;
      
      // Set canvas size based on its actual display size
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * 2; // Scale for retina displays
      canvas.height = rect.height * 2;
      
      const context = canvas.getContext('2d');
      context.scale(2, 2); // Scale for retina/high DPI displays
      context.lineCap = 'round';
      context.strokeStyle = '#0F172A'; // Dark blue color
      context.lineWidth = 2;
      contextRef.current = context;
      
      console.log('Canvas initialized:', {
        width: canvas.width,
        height: canvas.height,
        displayWidth: rect.width,
        displayHeight: rect.height
      });
    }
  };

  // Handle drawing functions
  const startDrawing = ({ nativeEvent }) => {
    // Ensure canvas is initialized before drawing
    if (!contextRef.current) {
      console.log('Canvas not initialized, initializing now...');
      initializeCanvas();
    }
    
    if (!contextRef.current) {
      console.error('Failed to initialize canvas context');
      return; // Safety check
    }
    
    const { offsetX, offsetY } = nativeEvent;
    console.log('Starting drawing at:', { offsetX, offsetY });
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing || !contextRef.current) return;
    
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.lineTo(offsetX, offsetY);
    contextRef.current.stroke();
  };

  const stopDrawing = () => {
    if (!contextRef.current) return;
    
    contextRef.current.closePath();
    setIsDrawing(false);
  };
  
  // Clear canvas content
  const clearCanvas = () => {
    if (!canvasRef.current || !contextRef.current) return;
    
    const canvas = canvasRef.current;
    // Use the stored context instead of getting a new one
    contextRef.current.clearRect(0, 0, canvas.width / 2, canvas.height / 2); // Divide by 2 because of scaling
    setHasDrawn(false);
    
    console.log('Canvas cleared');
  };
  
  // Handle file upload for image signature
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageSignature(event.target.result);
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Create and convert typed signature to image data URL
  const createTypedSignatureImage = () => {
    if (!typedSignature.trim()) return null;
    
    // Create a temporary canvas to render the text
    const canvas = document.createElement('canvas');
    canvas.width = 600; // Maximum width
    canvas.height = 200; // Maximum height
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'transparent';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set font options
    const fontSize = 48;
    const selectedFont = availableFonts.find(f => f.name === typedSignatureFont);
    ctx.font = `${fontSize}px ${selectedFont?.css || "'Inter', sans-serif"}`;
    ctx.fillStyle = '#0F172A'; // Dark blue text
    
    // Center the text
    const textWidth = ctx.measureText(typedSignature).width;
    const x = (canvas.width - textWidth) / 2;
    const y = canvas.height / 2 + fontSize / 3; // Adjust for baseline

    ctx.fillText(typedSignature, x, y);
    
    return canvas.toDataURL('image/png');
  };
  
  // Save and prepare the signature for placement
  const saveSignature = () => {
    let signatureData;
    
    if (signatureType === 'drawn') {
      if (!hasDrawn) return; // Don't save if nothing was drawn
      signatureData = canvasRef.current.toDataURL('image/png');
    } else if (signatureType === 'typed') {
      if (!typedSignature.trim()) return; // Don't save if no text
      signatureData = createTypedSignatureImage();
    } else if (signatureType === 'image') {
      if (!imageSignature) return; // Don't save if no image
      signatureData = imageSignature;
    }
    
    if (signatureData) {
      setCreatedSignatureData(signatureData);
      setCreatedSignatureType(signatureType);
      
      // If we have document data, move to placement tab
      if (documentData) {
        setActiveTab('place');
      } else {
        // If no document data, just return the signature
        onSignatureCreated(signatureData, signatureType);
      }
    }
  };
  
  // DOCUMENT PLACEMENT FUNCTIONS
  
  // Handle page navigation
  const changePage = (delta) => {
    const newPage = currentPage + delta;
    if (numPages && newPage >= 1 && newPage <= numPages) {
      setCurrentPage(newPage);
    }
  };
  
  // Handle zoom adjustments
  const changeZoom = (delta) => {
    const newZoom = Math.max(0.5, Math.min(2, zoom + delta));
    setZoom(newZoom);
  };
  
  // PDF document loaded successfully
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };
  
  // Handle mousedown for signature dragging
  const handleMouseDown = (e) => {
    if (!createdSignatureData) return;
    
    const signature = signatureRef.current;
    if (signature && (e.target === signature || signature.contains(e.target))) {
      setIsDragging(true);
      
      const rect = signature.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      
      e.preventDefault();
    }
  };
  
  // Handle mousemove for signature dragging
  const handleMouseMove = (e) => {
    if (isDragging && signatureRef.current && documentRef.current) {
      const docRect = documentRef.current.getBoundingClientRect();
      
      // Calculate new position in document coordinate space (unscaled)
      const newX = Math.max(0, Math.min(
        (e.clientX - docRect.left - dragOffset.x) / zoom,
        (docRect.width / zoom) - (signatureRef.current.offsetWidth / zoom)
      ));
      
      const newY = Math.max(0, Math.min(
        (e.clientY - docRect.top - dragOffset.y) / zoom,
        (docRect.height / zoom) - (signatureRef.current.offsetHeight / zoom)
      ));
      
      setSignaturePosition({ x: newX, y: newY });
    }
  };
  
  // Handle mouseup to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Apply signature to document with current position and page
  const applySignature = () => {
    if (!createdSignatureData) {
      setError('Please create a signature first');
      return;
    }
    
    // If we have an initial field, prioritize that
    if (initialFieldRef.current) {
      onSignatureCreated({
        signatureData: createdSignatureData,
        signatureType: createdSignatureType,
        fieldId: initialFieldRef.current.id,
        position: { 
          x: initialFieldRef.current.x || 100, 
          y: initialFieldRef.current.y || 100 
        },
        page: currentPage
      });
    }
    // Check if we have pre-defined signature fields
    else if (signatureFields && signatureFields.length > 0) {
      // For fields use case, return with signature data and field info
      const fieldsOnCurrentPage = signatureFields.filter(field => 
        field.page === currentPage || (field.page === undefined && currentPage === 1)
      );
      
      if (fieldsOnCurrentPage.length > 0) {
        // Use the field where the signature is currently positioned
        // Find the closest field to the current signature position
        let closestField = fieldsOnCurrentPage[0];
        let closestDistance = Number.MAX_VALUE;
        
        fieldsOnCurrentPage.forEach(field => {
          const distance = Math.sqrt(
            Math.pow(field.x - signaturePosition.x, 2) + 
            Math.pow(field.y - signaturePosition.y, 2)
          );
          
          if (distance < closestDistance) {
            closestDistance = distance;
            closestField = field;
          }
        });
        
        onSignatureCreated({
          signatureData: createdSignatureData,
          signatureType: createdSignatureType,
          fieldId: closestField.id,
          position: { x: closestField.x, y: closestField.y },
          page: currentPage
        });
      } else {
        // No fields on current page, fall back to current position
        onSignatureCreated({
          signatureData: createdSignatureData,
          signatureType: createdSignatureType,
          position: signaturePosition,
          page: currentPage
        });
      }
    } else {
      // For free placement, return with position
      onSignatureCreated({
        signatureData: createdSignatureData,
        signatureType: createdSignatureType,
        position: signaturePosition,
        page: currentPage
      });
    }
  };
  
  // Set up event listeners for mouse events for dragging
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);
  
  // Don't show the UI until we've checked for default signature
  if (isLoadingDefault) {
    return (
      <div className="bg-white rounded-lg border border-gray-300 w-full p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-3"></div>
          <span className="text-gray-600">Loading signature...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-300 w-full">
      {/* Tabs for Create/Place modes */}
      {documentData && (
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            className={`flex-1 p-3 text-sm font-medium ${
              activeTab === 'create' ? 'bg-white border-b-2 border-blue-500 text-gray-900' : 'hover:bg-gray-100 text-gray-600'
            }`}
            onClick={() => setActiveTab('create')}
          >
            Create Signature
          </button>
          <button
            className={`flex-1 p-3 text-sm font-medium ${
              activeTab === 'place' ? 'bg-white border-b-2 border-blue-500 text-gray-900' : 'hover:bg-gray-100 text-gray-600'
            }`}
            onClick={() => setActiveTab('place')}
            disabled={!createdSignatureData}
          >
            Place Signature
          </button>
        </div>
      )}
      
      {/* CREATE SIGNATURE TAB */}
      {activeTab === 'create' && (
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">Create Your Signature</h2>
          
          {/* Default Signature Display */}
          {isLoadingDefault && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                <span className="text-sm text-gray-600">Loading your default signature...</span>
              </div>
            </div>
          )}
          
          {useDefaultSignature && defaultSignature && (
            <div className="mb-6">
              <div className="text-center mb-6">
                <p className="text-lg font-medium text-gray-900 mb-4">Using your default signature</p>
                <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 inline-block">
                  <img 
                    src={defaultSignature.data} 
                    alt="Your default signature" 
                    className="max-h-24 max-w-64 mx-auto"
                  />
                </div>
                <p className="text-sm text-gray-600 capitalize mt-3">
                  Type: {defaultSignature.type}
                  {defaultSignature.font && ` • Font: ${defaultSignature.font}`}
                </p>
              </div>
              
              <div className="flex justify-center space-x-4 mb-6">
                <button
                  onClick={() => {
                    setUseDefaultSignature(false);
                    setCreatedSignatureData(null);
                    setCreatedSignatureType(null);
                    setSignatureType('drawn');
                    setTypedSignature('');
                    setImageSignature(null);
                    setImagePreview(null);
                    setHasDrawn(false);
                    setLegalCheckboxChecked(false);
                    clearCanvas();
                  }}
                  className="px-6 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                >
                  Create New
                </button>
                <button
                  onClick={() => window.open('/settings?tab=signature', '_blank')}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center font-medium"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Default
                </button>
              </div>

                             {/* Legal Binding Checkbox */}
               <div className="border-t border-gray-200 pt-4">
                 <label className="flex items-start space-x-3 cursor-pointer">
                   <input
                     type="checkbox"
                     checked={legalCheckboxChecked}
                     onChange={(e) => setLegalCheckboxChecked(e.target.checked)}
                     className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                   />
                   <span className="text-sm text-gray-700 leading-5">
                     I acknowledge that I have thoroughly reviewed this agreement in its entirety and understand its contents. 
                     I understand that this signature is legally binding and represents my agreement to the terms and conditions outlined in this document.
                   </span>
                 </label>
               </div>

               {/* Action buttons for default signature */}
               <div className="flex justify-between mt-6">
                 <button
                   onClick={onCancel}
                   className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700"
                 >
                   Cancel
                 </button>
                 <button
                   onClick={() => {
                     if (documentData) {
                       setActiveTab('place');
                     } else {
                       onSignatureCreated(createdSignatureData, createdSignatureType);
                     }
                   }}
                   disabled={!legalCheckboxChecked}
                   className="px-4 py-2 bg-blue-600 rounded-md text-white flex items-center hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   <Check size={18} className="mr-2" />
                   {documentData ? "Continue to Placement" : "Complete Signature"}
                 </button>
               </div>
            </div>
          )}
          
          {!useDefaultSignature && (
            <>
          {/* Signature type selector */}
          <div className="flex border border-gray-300 rounded-md mb-6">
            <button
              className={`flex-1 p-3 text-sm flex items-center justify-center ${
                signatureType === 'drawn' ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'
              }`}
              onClick={() => setSignatureType('drawn')}
            >
              <Pen size={18} className="mr-2" />
              Draw
            </button>
            <button
              className={`flex-1 p-3 text-sm flex items-center justify-center ${
                signatureType === 'typed' ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'
              }`}
              onClick={() => setSignatureType('typed')}
            >
              <Type size={18} className="mr-2" />
              Type
            </button>
            <button
              className={`flex-1 p-3 text-sm flex items-center justify-center ${
                signatureType === 'image' ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'
              }`}
              onClick={() => setSignatureType('image')}
            >
              <Upload size={18} className="mr-2" />
              Upload
            </button>
          </div>
          
          {/* Drawing Canvas */}
          {signatureType === 'drawn' && (
            <div className="mb-4">
              <div className="relative">
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  className="border border-gray-300 rounded-md w-full h-40 bg-white cursor-crosshair"
                  style={{ touchAction: 'none', width: '100%', height: '160px' }} // Prevent scrolling on touch devices
                />
                <button
                  onClick={clearCanvas}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  title="Clear"
                >
                  <Trash size={16} />
                </button>
                {!hasDrawn && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-gray-400 text-sm bg-white px-2 py-1 rounded shadow-sm">
                      Click and drag to draw your signature
                    </div>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-2">Draw your signature using your mouse or touch screen</p>
            </div>
          )}
          
          {/* Typed Signature */}
          {signatureType === 'typed' && (
            <div className="mb-4">
              <input
                type="text"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                placeholder="Type your name"
                className="w-full p-3 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4 text-gray-900"
              />
              
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-2">Signature Style</label>
                
                {/* Font Category Tabs */}
                <div className="flex mb-3 border-b border-gray-200">
                  {['professional', 'elegant', 'modern', 'casual', 'classic'].map(category => (
                    <button
                      key={category}
                      onClick={() => setFontCategory(category)}
                      className={`px-3 py-2 text-sm font-medium capitalize transition-colors ${
                        fontCategory === category 
                          ? 'text-blue-600 border-b-2 border-blue-600' 
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
                
                {/* Font Grid Preview */}
                <div className="grid grid-cols-2 gap-2 mb-4 max-h-48 overflow-y-auto" key={fontCategory}>
                  {availableFonts
                    .filter(font => font.category === fontCategory)
                    .map(font => (
                      <button
                        key={font.name}
                        onClick={() => setTypedSignatureFont(font.name)}
                        className={`p-3 border rounded-md text-left transition-all ${
                          typedSignatureFont === font.name
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-xs text-gray-500 mb-1">{font.name}</div>
                        <div 
                          className="text-lg truncate"
                          style={{ fontFamily: font.css }}
                          key={`${font.name}-${fontCategory}`}
                        >
                          {typedSignature || 'Sample'}
                        </div>
                      </button>
                    ))}
                </div>
              </div>
              
              {typedSignature && (
                <div className="p-4 bg-gray-50 rounded-md border border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Preview:</p>
                  <div
                    className="text-center text-3xl text-gray-900 whitespace-nowrap overflow-hidden"
                    style={{ fontFamily: availableFonts.find(f => f.name === typedSignatureFont)?.css || "'Inter', sans-serif" }}
                  >
                    {typedSignature}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Image Upload */}
          {signatureType === 'image' && (
            <div className="mb-4">
              <label className="block w-full p-3 bg-gray-50 border border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:bg-gray-100">
                <span className="block mb-2 text-gray-700">Click to upload your signature image</span>
                <span className="text-sm text-gray-500">JPG, PNG or GIF formats accepted</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              
              {imagePreview && (
                <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
                  <img 
                    src={imagePreview} 
                    alt="Signature preview" 
                    className="max-h-32 mx-auto"
                  />
                </div>
              )}
            </div>
          )}
          
          {/* Legal Binding Checkbox for traditional signature creation */}
          <div className="border-t border-gray-200 pt-4 mt-6">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={legalCheckboxChecked}
                onChange={(e) => setLegalCheckboxChecked(e.target.checked)}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700 leading-5">
                I acknowledge that I have thoroughly reviewed this agreement in its entirety and understand its contents. 
                I understand that this signature is legally binding and represents my agreement to the terms and conditions outlined in this document.
              </span>
            </label>
          </div>

          {/* Action buttons */}
          <div className="flex justify-between mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={saveSignature}
              disabled={(signatureType === 'drawn' && !hasDrawn) || 
                      (signatureType === 'typed' && !typedSignature.trim()) ||
                      (signatureType === 'image' && !imageSignature) ||
                      !legalCheckboxChecked}
              className="px-4 py-2 bg-blue-600 rounded-md text-white flex items-center hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check size={18} className="mr-2" />
              {documentData ? "Continue to Placement" : "Save Signature"}
            </button>
          </div>
          </>
          )}
        </div>
      )}
      
      {/* PLACE SIGNATURE TAB */}
      {activeTab === 'place' && documentData && (
        <div className="flex flex-col h-full">
          {/* Header for placement tab */}
          <div className="bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Place Your Signature</h3>
            <div className="flex items-center">
              {/* Page navigation */}
              <div className="flex items-center mr-4">
                <button
                  onClick={() => changePage(-1)}
                  disabled={currentPage <= 1}
                  className="p-1 rounded-md hover:bg-gray-200 disabled:opacity-50 text-gray-600"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="mx-2 text-sm text-gray-700">
                  Page {currentPage} of {numPages || '?'}
                </span>
                <button
                  onClick={() => changePage(1)}
                  disabled={!numPages || currentPage >= numPages}
                  className="p-1 rounded-md hover:bg-gray-200 disabled:opacity-50 text-gray-600"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
              
              {/* Zoom controls */}
              <div className="flex items-center">
                <button
                  onClick={() => changeZoom(-0.1)}
                  disabled={zoom <= 0.5}
                  className="p-1 rounded-md hover:bg-gray-200 disabled:opacity-50 text-gray-600"
                >
                  <ZoomOut size={18} />
                </button>
                <span className="mx-2 text-sm text-gray-700">{Math.round(zoom * 100)}%</span>
                <button
                  onClick={() => changeZoom(0.1)}
                  disabled={zoom >= 2}
                  className="p-1 rounded-md hover:bg-gray-200 disabled:opacity-50 text-gray-600"
                >
                  <ZoomIn size={18} />
                </button>
              </div>
            </div>
          </div>
          
          {/* Document placement area */}
          <div className="flex-1 overflow-auto bg-gray-100 p-4" onMouseDown={handleMouseDown}>
            {error && (
              <div className="flex items-center justify-center h-full">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md">
                  <div className="flex items-center text-red-600 mb-2">
                    <AlertCircle size={20} className="mr-2" />
                    <span className="font-semibold">Error</span>
                  </div>
                  <p className="text-red-500">{error}</p>
                </div>
              </div>
            )}
            
            {!error && (
              <div 
                ref={documentRef}
                className="relative mx-auto my-4"
              >
                {documentData?.url ? (
                  <Document
                    file={documentData.url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    }
                    error={
                      <div className="flex items-center justify-center py-20 text-red-600">
                        <AlertCircle size={24} className="mr-2" />
                        <span>Failed to load PDF document</span>
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={currentPage} 
                      scale={zoom}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                ) : (
                  <div 
                    className="bg-white shadow-lg mx-auto"
                    style={{ 
                      width: `${8.5 * 96 * zoom}px`, // 8.5 x 11 inches at 96 DPI
                      height: `${11 * 96 * zoom}px`,
                      transform: `scale(${zoom})`,
                      transformOrigin: 'top center'
                    }}
                  >
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-gray-500">Document Preview - Page {currentPage}</span>
                    </div>
                  </div>
                )}
                
                {/* Signature overlay */}
                {createdSignatureData && (
                  <div
                    ref={signatureRef}
                    className="absolute cursor-move"
                    style={{
                      left: `${signaturePosition.x * zoom}px`,
                      top: `${signaturePosition.y * zoom}px`,
                      zIndex: 50,
                      border: isDragging ? '2px dashed #3b82f6' : '2px solid transparent',
                      padding: '4px'
                    }}
                  >
                    <img 
                      src={createdSignatureData} 
                      alt="Your signature" 
                      style={{
                                maxWidth: '300px',
        maxHeight: '150px'
                      }}
                      draggable="false"
                    />
                  </div>
                )}
                
                {/* Pre-placed signature fields */}
                {signatureFields.length > 0 && (
                  <div className="absolute inset-0 pointer-events-none">
                    {signatureFields
                      .filter(field => field.page === currentPage || (field.page === undefined && currentPage === 1))
                      .map((field, index) => {
                        // Set reasonable defaults for missing values
                        const x = field.x || 100;
                        const y = field.y || 100;
                            const width = field.width || 300;
    const height = field.height || 150;
                        
                        // Determine if this field is the closest to current signature position
                        let isActiveField = false;
                        if (createdSignatureData) {
                          const distance = Math.sqrt(
                            Math.pow(x - signaturePosition.x, 2) + 
                            Math.pow(y - signaturePosition.y, 2)
                          );
                          
                          // If signature position is within 50px of this field's center (in document coordinates), consider it active
                          isActiveField = distance < 50;
                        }
                        
                        return (
                          <div
                            key={index}
                            className={`absolute border-4 rounded-md flex items-center justify-center ${
                              isActiveField 
                                ? "border-green-500 bg-green-100 bg-opacity-40" 
                                : "border-blue-500 bg-blue-100 bg-opacity-30"
                            }`}
                            style={{
                              left: `${x * zoom}px`,
                              top: `${y * zoom}px`,
                              width: `${width * zoom}px`,
                              height: `${height * zoom}px`,
                              transformOrigin: 'top left',
                              transition: 'border-color 0.2s, background-color 0.2s'
                            }}
                            onClick={() => {
                              if (createdSignatureData) {
                                setSignaturePosition({ x, y });
                              }
                            }}
                          >
                            <div className={`text-sm text-center p-2 ${
                              isActiveField ? "text-green-600" : "text-blue-600"
                            }`}>
                              {isActiveField && createdSignatureData 
                                ? "Signature Will Be Applied Here" 
                                : (field.label || "Signature Required")}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Footer with actions */}
          <div className="bg-gray-50 border-t border-gray-200 p-4">
            {/* Legal Agreement Checkbox */}
            <div className="mb-4 p-3 bg-white border border-amber-200 rounded-lg">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasReadContract}
                  onChange={(e) => setHasReadContract(e.target.checked)}
                  className="mt-1 mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">I acknowledge that:</span> 
                  <br />• I have read and understand the entire contract/document
                  <br />• This electronic signature is legally binding and has the same legal effect as a handwritten signature
                  <br />• I am authorized to sign this document on behalf of myself or my organization
                </span>
              </label>
            </div>
            
            <div className="flex justify-between">
              <button
                onClick={() => setActiveTab('create')}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 text-gray-700"
              >
                Back to Create
              </button>
              <div>
                <button
                  onClick={onCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 mr-3 text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={applySignature}
                  disabled={!hasReadContract}
                  className="px-4 py-2 bg-blue-600 rounded-md text-white hover:bg-blue-500 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check size={18} className="mr-2" />
                  Apply Signature
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedSignatureCanvas;